<template>
  <div v-if="preference" class="preference-item">
    <div class="preference-item__label">
      <div class="preference-item__name">{{ preference.text }}</div>
      <div class="preference-item__description">{{ preference.subtext }}</div>
    </div>

    <div class="preference-item__counter" v-if="displayQuantity && showCounter">
      <button
        @click.prevent="decrement"
        :aria-label="`Decrement value. Current quantity: ${displayQuantity}`"
        class="preference-item__counter-trigger preference-item__counter-trigger--left btn">
        <svg class="preference-item__counter-icon" width="16" height="2" viewBox="0 0 16 2" xmlns="http://www.w3.org/2000/svg">
          <rect fill="currentColor" width="16" height="2" rx="1" />
        </svg>
      </button>
      <div
        v-if="pricedByWeight"
        class="preference-item__counter-label">
        {{ `${displayQuantity}${unit ? ' ' + unit : ''}` }}
      </div>
      <input
        v-else
        @blur="resetEmptyInput"
        @keyup="updateQuantity"
        class="preference-item__counter-input"
        type="number"
        :value="displayQuantity"
        :aria-label="`Change current quantity. Current quantity: ${displayQuantity}`"
      />
      <button
        @click.prevent="increment"
        :aria-label="`Increment value. Current quantity: ${displayQuantity}`"
        :disabled="addOnLimitReached"
        class="preference-item__counter-trigger preference-item__counter-trigger--right btn"
        :class="{ 'preference-item__counter-trigger--disabled': (maximumQuantityReached || maximumWeightReached) }">
        <svg class="preference-item__counter-icon" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M8 0C7.44772 0 7 0.447715 7 1V7H1C0.447715 7 0 7.44772 0 8C0 8.55229 0.447715 9 1 9H7V15C7 15.5523 7.44772 16 8 16C8.55229 16 9 15.5523 9 15V9H15C15.5523 9 16 8.55229 16 8C16 7.44772 15.5523 7 15 7H9V1C9 0.447715 8.55229 0 8 0Z" />
        </svg>
      </button>
    </div>

    <button
      v-else-if="displayQuantity && !showCounter"
      @click.prevent="showQuantityCounter"
      class="preference-item__button preference-item__quantity"
      :aria-label="`Change the quantity of this item. Current quantity: ${displayQuantity}${unit}`">
        {{ `${displayQuantity}${pricedByWeight && unit ? ' ' + unit : ''}` }}
    </button>

    <button
      v-else
      @click.prevent="showQuantityCounter"
      :disabled="addOnLimitReached"
      class="preference-item__button preference-item__add"
      aria-label="Add this item to your cart.">
      <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0 10C0 15.5228 4.47715 20 10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10ZM9 4C9 3.44772 9.44772 3 10 3C10.5523 3 11 3.44772 11 4V9H16C16.5523 9 17 9.44772 17 10C17 10.5523 16.5523 11 16 11H11V16C11 16.5523 10.5523 17 10 17C9.44772 17 9 16.5523 9 16V11H4C3.44772 11 3 10.5523 3 10C3 9.44772 3.44772 9 4 9H9V4Z"/>
      </svg>
    </button>
  </div>
</template>

<script>
import _ from 'lodash';
import errorMessaging from '../mixins/errorMessaging';
import { PRODUCT_ADD_TO_CART_EVENT, PRODUCT_FIRST_ADD_TO_CART_EVENT } from "../../utilities/constants";

export default {
  name: 'PreferenceItem',
  mixins: [errorMessaging],
  props: {
    preference: {
      type: Object
    },
    product: {
      type: Object
    },
    bucket: {
      type: String
    },
    totalQuantity: {
      type: Number
    },
    listname: {
      type: String,
      default: ''
    },
    buttonType: {
      type: String,
      default:  'spa' // spa, nonspa
    },
    size: {
      type: String,
      default: 'normal' // normal, large
    },
  },
  data() {
    return {
      displayQuantity: 0,
      showCounter: false
    };
  },
  computed: {
    productId() {
      return this.product.id;
    },
    skuId() {
      return this.product.defaultSku.id;
    },
    pricedByWeight() {
      return this.product.pricedByWeight;
    },
    unit() {
      return this.product.defaultSku.priceInfo.listPrice.unit;
    },
    weightSelectionIncrements() {
      return this.product.defaultSku.weightSelectionIncrements;
    },
    hasCoupon() {
      return this.product.defaultSku.availableCouponIds.length;
    },
    commerceItem() {
      if (typeof this.$store.state[this.bucket] === 'undefined') {
        return;
      }

      if (this.bucket === 'cart') {
        let commerceItem;

        this.$store.state.cart.commerceItems.forEach(
          category => {
            const item = category.items.find(
              item =>
                item.product.id === this.productId &&
                item.purchasePreference &&
                item.purchasePreference.preferenceId &&
                item.purchasePreference.preferenceId === this.preference.preferenceId
            );

            if (item) {
              commerceItem = item;
            }
          }
        );

        return commerceItem;
      }

      if (this.bucket === 'orderAddOns') {
        return this.$store.state.orderAddOns.orderAddOn.commerceItems.find(
          item =>
            item.product.id === this.productId &&
            item.purchasePreference &&
            item.purchasePreference.preferenceId &&
            item.purchasePreference.preferenceId === this.preference.preferenceId
        );
      }
    },
    commerceItemQuantity() {
      if (this.commerceItem) {
        return this.pricedByWeight ? this.commerceItem.estimatedWeight : this.commerceItem.quantity;
      }
      return 0;
    },
    maximumQuantityReached() {
      return this.displayQuantity >= this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY;
    },
    addOnLimitReached() {
      return (
        this.bucket === 'orderAddOns' &&
        this.$store.state.orderAddOns.orderAddOn.remainingAddOnItems === 0 &&
        (!this.pricedByWeight || this.displayQuantity <= 0)
      );
    },
    maximumWeightReached() {
      const maxWeightReached =
        this.pricedByWeight &&
        (this.weightSelectionIncrements.indexOf(this.displayQuantity) === this.weightSelectionIncrements.length - 1);

      return maxWeightReached;
    }
  },
  methods: {
    showQuantityCounter() {
      if (!this.displayQuantity) {
        this.increment();
      }
      this.showCounter = true;
    },
    increment() {
      if (this.maximumQuantityReached || this.maximumWeightReached) {
        const errorMessage = this.pricedByWeight ?
          'This option has reached its maximum order weight' :
          'This option has reached its maximum order quantity';
        this.$emit('update-error-message', errorMessage);
        return;
      }

      if (this.pricedByWeight) {
        const oldQuantityIndex = this.displayQuantity ?
          this.weightSelectionIncrements.indexOf(this.displayQuantity) :
          -1;
        const newQuantity = this.weightSelectionIncrements[oldQuantityIndex + 1];
        this.displayQuantity = newQuantity;
        this.debouncedUpdateQuantity(newQuantity);
      } else {
        if (this.displayQuantity === 0) {
          this.addItemToBucket(1);
          return;
        }

        const newQuantity = this.displayQuantity + 1;
        this.displayQuantity = newQuantity;
        this.debouncedUpdateQuantity(newQuantity);
      }
    },
    decrement() {
      if (this.pricedByWeight) {
        const oldQuantityIndex = this.weightSelectionIncrements.indexOf(this.displayQuantity);
        if (oldQuantityIndex === 0) {
          this.displayQuantity = 0;
          this.debouncedUpdateQuantity(0);
        } else {
          const newQuantity = this.weightSelectionIncrements[oldQuantityIndex - 1];
          this.displayQuantity = newQuantity;
          this.debouncedUpdateQuantity(newQuantity);
        }
      } else {
        const newQuantity = this.displayQuantity - 1;
        this.displayQuantity = newQuantity;
        this.debouncedUpdateQuantity(newQuantity);
      }
    },
    resetEmptyInput(e) {
      if (!e.target.value) {
        e.target.value = this.commerceItemQuantity;
      }
    },
    updateQuantity(e) {
      if (!e.target.value) {
        return;
      }

      var newQuantity = parseInt(e.target.value.replace(/[^0-9.]/g, ''));
      if (newQuantity === this.commerceItemQuantity) {
        return;
      }

      if (newQuantity > this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY) {
        newQuantity = this.$store.state.config.constants.UI_MAX_ORDER_QUANTITY;
      }

      this.debouncedUpdateQuantity(newQuantity);
    },
    addItemToBucket(quantity) {
      const originalTotalQuantity = this.totalQuantity;

      const item = {
        productId: this.productId,
        skuId: this.skuId,
        purchasePreferenceId: this.preference.preferenceId,
      };

      if (this.pricedByWeight) {
        item.weight = quantity;
      } else {
        item.quantity = quantity;
      }

      this.trackAddToCart(quantity);
      this.$emit('update-error-message', null);
      this.$store.dispatch(this.bucket + '/ADD_ITEM_TO_BUCKET', item).then(response => {
        if (response.status === 200) {
          if (this.hasCoupon && originalTotalQuantity === 0) {
            this.$emit('show-coupon-on-close');
          }

          this.trackFirstAddToCart(quantity);
        } else {
          this.displayQuantity = this.commerceItemQuantity;
          this.$emit('update-error-message', this.getShortErrorMessage(response));
        }
      });
    },
    removeItemFromBucket() {
      this.$emit('update-error-message', null);
      this.$store.dispatch(this.bucket + '/DELETE_ITEM', this.commerceItem.id).then(response => {
        if (response && response.status !== 200){
          this.displayQuantity = this.commerceItemQuantity;
          this.$emit('update-error-message', this.getShortErrorMessage(response));
        }
      });
    },
    updateItemQuantity(quantity) {
      const item = {
        id: this.commerceItem.id,
        quantity: quantity
      };

      this.trackAddToCart(quantity);
      this.$emit('update-error-message', null);
      this.$store.dispatch(this.bucket + '/PUT_ITEM_QUANTITY', item).then(response => {
        if (response.status !== 200){
          this.displayQuantity = this.commerceItemQuantity;
          this.$emit('update-error-message', this.getShortErrorMessage(response));
        }
      });
    },
    trackAddToCart(newQuantity) {
      this.gtmClick(
        PRODUCT_ADD_TO_CART_EVENT,
        this.product,
        this.bucket,
        this.listname,
        newQuantity,
        this.buttonType,
        1,
        'default',
        {
          preference: {
            selected: this.preference.preferenceId !== 'default',
            id: this.preference.preferenceId,
            text: this.preference.text,
            quantity: newQuantity,
          }
        }
      );
    },
    trackFirstAddToCart(newQuantity) {
      this.gtmClick(
        PRODUCT_FIRST_ADD_TO_CART_EVENT,
        this.product,
        this.bucket,
        this.listname,
        newQuantity,
        this.buttonType,
        1,
        'default',
        {
          preference: {
            selected: this.preference.preferenceId !== 'default',
            id: this.preference.preferenceId,
            text: this.preference.text,
            quantity: newQuantity,
          },
          isFirstAddToCart: true
        }
      );
    },
  },
  created() {
    this.debouncedUpdateQuantity = _.debounce(quantity => {
      if (quantity <= 0) {
        this.removeItemFromBucket();
        return;
      }

      if (this.pricedByWeight) {
        this.addItemToBucket(quantity);
      } else {
        this.updateItemQuantity(quantity);
      }
    }, 500);
  },
  watch: {
    commerceItemQuantity: {
      immediate: true,
      handler(newValue) {
        this.displayQuantity = newValue;
      },
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/lib";
@import "../../../../static-assets/css/_btn.scss";
@import "../../../../static-assets/css/_product-card.scss";

.preference-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.1px;
  height: 75px;
  margin: 0px 25px 0px 15px;

  &:not(:last-child) {
    border-bottom: 1px solid #e8e8e8;
  }
}

.preference-item__label {
  overflow: hidden;
  margin-right: 8px;
}

.preference-item__name {
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.preference-item__description {
  color: #424046;
  font-weight: 300;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.preference-item__button {
  padding: 0;
  border: 0;
  margin: 0;
  background: transparent;
  line-height: 1;
  display: flex;
  min-height: 24px;
  min-width: 24px;

  &:disabled {
    cursor: default;
  }
}

.preference-item__quantity {
  background-color: $color-button-blue;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  min-width: 24px;
  height: 24px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  line-height: 1;
  margin: 0;
}

.preference-item__add {
  fill: #414142;

  &:disabled {
    fill: #d7d7d8;
  }
}

.preference-item__counter {
  display: flex;
  position: absolute;
  right: 0;
}

.preference-item__counter-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  margin: 0;
  height: 40px;
  border: 1px solid #c5c5c5;
  color: #424046;
  background-color: #ffffff;
  width: 40px;
  min-width: 40px;
  min-height: 40px;

  &:hover,
  &:focus,
  &:active,
  &:disabled {
    background-color: #e6e6e6;
    color: #424046;
  }

  &:disabled,
  &.preference-item__counter-trigger--disabled {
    cursor: default;
  }
}

.preference-item__counter-trigger--left {
  border-radius: 8px 0 0 8px;
}

.preference-item__counter-trigger--right {
  border-radius: 0 8px 8px 0;
}

.preference-item__counter-icon {
  height: 10px;
  width: 10px;
}

.preference-item__counter-label {
  height: 40px;
  width: 60px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #c5c5c5;
  border-width: 1px 0;
  border-radius: 0;
  padding: 0px;
  background-color: #fdfdfd;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preference-item__counter-input {
  height: 40px;
  width: 40px;
  color: #424046;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #c5c5c5;
  border-width: 1px 0;
  border-radius: 0;
  padding: 0px;
  background-color: #fdfdfd;
  box-shadow: none;
  box-sizing: border-box;
  text-align: center;
}
</style>